import BaseRepository from '@/core/standard/BaseRepository';
import {axiosInstance} from '@/setup/axios/AxiosConfig';
import {ElementURL, GenericMethod, UrlParams} from '@/core/Types/IRepository';

class ReportsServices extends BaseRepository {
  public collection = 'reports';
  public version = 'v1';

  public getAllBoxReports(params: GenericMethod) {
    return this.request({...params, url: 'box_report'});
  }

  public closeBox(params: GenericMethod) {
    return this.request({...params, url: 'close_box'});
  }

  public printBox(params: GenericMethod) {
    return this.request({...params, url: 'print_box'});
  }

  public updateCashRegister(params: GenericMethod) {
    return this.request({...params, url: 'update_cash_register'});
  }

  public productReport(params: GenericMethod) {
    return this.request({...params, url: 'products_report'});
  }

  public stockReport(params: GenericMethod) {
    return this.request({...params, url: 'stock'});
  }

  public requestsHistoriesReport(params: GenericMethod) {
    return this.request({...params, url: 'requests_histories'});
  }

  public salesReport(params: GenericMethod) {
    return this.request({...params, url: 'sales'});
  }

  public downloadProductsFile(params: GenericMethod) {
    return this.request({...params, url: 'download/products'});
  }

  public downloadSalesFile(params: GenericMethod) {
    return this.request({...params, url: 'download/sales'});
  }

  public downloadBoxesFile(params: GenericMethod) {
    return this.request({...params, url: 'download/box_report'});
  }

  public getSalesGraphData(params: GenericMethod) {
    return this.request({...params, url: 'dashboard/sales', method: 'get'});
  }

  public getAverageTicketGraphData(params: GenericMethod) {
    return this.request({...params, url: 'dashboard/average_ticket', method: 'get'});
  }

  public getHourlyBillingGraphData(params: GenericMethod) {
    return this.request({...params, url: 'dashboard/hourly_billing', method: 'post'});
  }

  public getExpensesGraphData(params: GenericMethod) {
    return this.request({...params, url: 'dashboard/expenses', method: 'get'});
  }

  public downloadHistoryFile(params: GenericMethod) {
    return this.request({...params, url: 'download/requests_history'});
  }

  public async getBy<T = any>(params: ElementURL<T>): Promise<any> {
    return super.request({...params, url: 'get', method: 'post'});
  }

  public async insert<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'insert', method: 'post'});
  }

  public async update<T = any>(params: ElementURL<T>): Promise<any> {
    return super.request({query: params.query, url: 'update', method: 'post'});
  }

  public async deleteBy<T = any>(params: ElementURL<T>): Promise<any> {
    return super.request({...params, url: 'delete', method: 'post'});
  }

  public async batchDelete<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'delete_all', method: 'post'});
  }

  public async restoreBy<T = any>(params: ElementURL<T>): Promise<any> {
    return super.request({...params, url: 'restore', method: 'post'});
  }

  public predictionsSalesReport(params: GenericMethod) {
    return this.request({...params, url: 'predictions_sales', method: 'post'});
  }

  public predictionsSalesGraph(params: GenericMethod) {
    return this.request({...params, url: 'predictions_sales_graph', method: 'post'});
  }

  public predictionsProductsReport(params: GenericMethod) {
    return this.request({...params, url: 'predictions_products', method: 'post'});
  }

  public predictionsProductsGraph(params: GenericMethod) {
    return this.request({...params, url: 'predictions_products_graph', method: 'post'});
  }

  public apriori(params: GenericMethod) {
    return this.request({...params, url: 'apriori'});
  }

  public probability(params: GenericMethod) {
    return this.request({...params, url: 'probability'});
  }

  public downloadPredictionSalesFile(params: GenericMethod) {
    return this.request({...params, url: 'download/prediction_sales'});
  }

  public downloadAprioriFile(params: GenericMethod) {
    return this.request({...params, url: 'download/apriori'});
  }

  public downloadProbabilityFile(params: GenericMethod) {
    return this.request({...params, url: 'download/probability'});
  }

  public downloadPredictionProductsFile(params: GenericMethod) {
    return this.request({...params, url: 'download/prediction_products'});
  }

  public async getWidgets<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'widgets', method: 'post'});
  }

  public async getExpenseReport<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'expenses_report', method: 'post'});
  }

  public async getEconomicSubscriptionReport<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'economic_subscriptions_report', method: 'post'});
  }

  public async getAllInvoiceReports<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'holded/list_documents', method: 'get'});
  } 

  public downloadInvoiceFile(params: GenericMethod) {
    return this.request({...params, url: 'holded/document_pdf'});
  }

  public async getAllConsumptionsReports<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'consumptions_report', method: 'post'});
  } 

  //Reports information services
  public async getSalesByMonth<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_month', method: 'get'});
  } 

  public async getSalesByDay<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_day', method: 'get'});
  } 

  public async getSalesByProducts<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_product', method: 'get'});
  } 

  public async getSalesByCategory<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_category', method: 'get'});
  } 

  public async getSalesByRequestType<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_order_type', method: 'get'});
  } 

  public async getSalesByClient<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_client', method: 'get'});
  } 

  public async getSalesByUser<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_employee', method: 'get'});
  } 

  public async getSalesByEmployeeProduct<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_employee_product', method: 'get'});
  }

  public async getSalesByPaymentMethod<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_payed_by', method: 'get'});
  }
  
  public async getSalesByLocales<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/sales_by_local', method: 'get'});
  } 

  public async getExpensesByDay<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/expenses_by_day', method: 'get'});
  } 

  public async getExpensesByMonth<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/expenses_by_month', method: 'get'});
  } 

  public async getExpensesByPaymentType<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/expenses_by_payed_with', method: 'get'});
  } 

  public async getExpensesByEmployee<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/expenses_by_employee', method: 'get'});
  } 

  public async getExpensesByLocales<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/expenses_by_local', method: 'get'});
  } 

  public async getDiscountsByDay<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/discounts_by_day', method: 'get'});
  } 

  public async getDiscountsByMonth<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/discounts_by_month', method: 'get'});
  } 

  public async getTipsByDay<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/tips_by_day', method: 'get'});
  }   

  public async getTipsByMonth<T = any>(params: UrlParams<T>): Promise<any> {
    return super.request({...params, url: 'informations/tips_by_month', method: 'get'});
  } 

  //Download reportInfo files
  public downloadSalesByDay(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_day'});
  }

  public downloadSalesByMonth(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_month'});
  }

  public downloadExpensesByDay(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_expenses_by_day'});
  }

  public downloadExpensesByMonth(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_expenses_by_month'});
  }
 
  public downloadDiscountsByDay(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_discounts_by_day'});
  }
  
  public downloadDiscountsByMonth(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_discounts_by_month'});
  }

  public downloadTipsByDay(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_tips_by_day'});
  }

  public downloadTipsByMonth(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_tips_by_month'});
  }

  public downloadExpensesByEmployee(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_expenses_by_employee'});
  }

  public downloadSalesByProducts(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_product'});
  }

  public downloadSalesByCategory(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_category'});
  }

  public downloadSalesByRequestType(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_order_type'});
  }

  public downloadSalesByClient(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_client'});
  }

  public downloadSalesByEmployee(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_employee'});
  }

  public downloadSalesByEmployeeProduct(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_employee_product'});
  }

  public downloadSalesByPaymentMethod(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_payed_by'});
  }

  public downloadSalesByLocales(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_sales_by_local'});
  }

  public downloadExpensesByLocales(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_expenses_by_local'});
  }

  public downloadExpensesByPaymentType(params: GenericMethod) {
    return this.request({...params, url: 'download_informations/download_expenses_by_payed_with'});
  }

   public downloadExpensesReport(params: GenericMethod) {
    return this.request({...params, url: 'download/expenses_report'});
  }

   public downloadEconomicSubscriptions(params: GenericMethod) {
    return this.request({...params, url: 'download/economic_subscriptions_report'});
  }
  public downloadConsumptions(params: GenericMethod) {
    return this.request({...params, url: 'download/consumptions_report'});
  }
   public downloadPurchases(params: GenericMethod) {
    return this.request({...params, url: 'download/purchase_report'});
  }

}

export default new ReportsServices(axiosInstance);

